import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import {
  LiliTextInput,
  LiliDropdown,
  LiliCheckbox,
  LiliTextInputProps,
  LiliDropdownProps,
  LiliCheckboxProps,
  LiliMaskedInputV2,
  LiliMask,
  LiliPhoneInput,
} from '@lili/lili-frontend-common';
import { ControllerWrapper } from '@Hooks/index';
import { normalizeInputSpacesOnBlur, normalizeInputSpacesOnChange } from '@Services/utils/utils.ts';
import { KeyFilterType } from 'primereact/keyfilter';
interface ControlledTextInputProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  title?: string;
  props?: LiliTextInputProps;
  id: string;
  accessoryItemComponent?: React.ReactNode;
  readonly?: boolean;
  tabindex?: number;
  disabled?: boolean;
  filterType?: KeyFilterType;
  max?: number;
}

export const ControlledTextInput = <T extends FieldValues>({
  control,
  name,
  title,
  id,
  props,
  accessoryItemComponent,
  readonly,
  tabindex,
  disabled,
  filterType,
  max,
}: ControlledTextInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliTextInput
          {...props}
          readonly={readonly}
          value={value}
          onBlur={() => {
            onChange(normalizeInputSpacesOnBlur(value));
            onBlur();
          }}
          onChange={(val) => onChange(normalizeInputSpacesOnChange(val))}
          id={id}
          title={title}
          errorMessage={error?.message}
          tabIndex={tabindex}
          filterType={filterType}
          disabled={disabled}
          maxLength={max}
          accessoryItemComponent={accessoryItemComponent}
        />
      )}
    />
  );
};

interface ControlledDropdownProps<T extends FieldValues> {
  control?: Control<T>;
  name: Path<T>;
  title: string;
  items: Array<{ title: string; value: string }>;
  id: string;
  props?: LiliDropdownProps;
  readonly?: boolean;
  tabIndex?: number;
  disabled?: boolean;
}

export const ControlledDropdown = <T extends FieldValues>({
  control,
  name,
  title,
  items,
  id,
  props,
  disabled,
  tabIndex,
}: ControlledDropdownProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
        return (
          <LiliDropdown
            {...props}
            value={items.find((item) => item.value === value?.value) || { title: '', value: '' }}
            onSelect={(item) => {
              onChange(item);
            }}
            onBlur={onBlur}
            onClear={() => onChange({ title: '', value: '' })}
            disabled={disabled}
            title={title}
            items={items}
            id={id}
            errorMessage={error?.message}
            tabIndex={tabIndex}
          />
        );
      }}
    />
  );
};

interface ControlledCheckboxProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  id: string;
  props?: LiliCheckboxProps;
  handleOnChange?: (value: boolean) => void;
}

export const ControlledCheckbox = <T extends FieldValues>({
  control,
  name,
  props,
  id,
  handleOnChange,
}: ControlledCheckboxProps<T>) => {
  return (
    <ControllerWrapper name={name} control={control}>
      {({ value, onChange, onBlur }) => (
        <LiliCheckbox
          {...props}
          id={id}
          checked={Boolean(value)}
          onBlur={onBlur}
          onCheckedChange={(isChecked) => {
            handleOnChange?.(isChecked);
            onChange(isChecked as T[Path<T>]);
          }}
        />
      )}
    </ControllerWrapper>
  );
};

interface ControlledMaskedInputProps<T extends FieldValues> extends ControlledTextInputProps<T> {
  mask: LiliMask;
}

export const ControlledMaskedInput = <T extends FieldValues>({
  control,
  name,
  title,
  id,
  props,
  readonly,
  mask,
}: ControlledMaskedInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliMaskedInputV2
          {...props}
          readonly={readonly}
          value={value}
          id={id}
          errorMessage={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          title={title}
          mask={mask}
        />
      )}
    />
  );
};

export const ControlledPhoneInput = <T extends FieldValues>({
  id,
  name,
  title,
  control,
  disabled,
  tabindex,
}: ControlledTextInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onBlur, onChange }, fieldState: { error } }) => (
        <LiliPhoneInput
          value={value}
          errorMessage={error?.message}
          onChange={onChange}
          onBlur={onBlur}
          id={id}
          title={title}
          disabled={disabled}
          tabIndex={tabindex}
        />
      )}
    />
  );
};
